import React, { useState } from "react";
import useAuthenticatedPhoto from "@/lib/useAuthenticatedPhoto";
import { boutiqueMeta } from "@/lib/api";

/**
 * Warm, on-brand avatar with a graceful photo → initials fallback.
 *
 * - If a photo URL is provided and loads, it's rendered.
 * - Otherwise we fall back to the teammate's initials on a quiet neutral surface.
 *   Boutique membership is carried by an explicit mark elsewhere, not by tinting every person.
 *
 * Props:
 *   name         string  — the teammate's full name (used for initials + alt text)
 *   picture      string? — absolute URL, or app-relative path (e.g. "/team/x.webp"). Optional.
 *   boutique     string? — "PLT" | "FLL" | "WYN" | "PNC" — drives the tint on the initials chip
 *   size         number  — pixel diameter (default 40)
 *   className    string? — extra classes for positioning
 *   ring         bool    — subtle 1px white ring for images floating on colored surfaces
 */
export default function Avatar({ name, picture, boutique, size = 40, className = "", ring = false }) {
  const [broken, setBroken] = useState(null);
  const src = useAuthenticatedPhoto(normalizeSrc(picture));
  const initials = initialsFrom(name);
  const bmeta = boutiqueMeta[boutique] || {};
  const bg = TINT[boutique] || TINT.Rainbow;
  const fg = FG[boutique] || FG.Rainbow;
  const ringCls = ring ? "ring-2 ring-white" : "";

  if (src && broken!==src) {
    return (
      <img
        src={src}
        onError={() => setBroken(src)}
        alt={name || ""}
        width={size}
        height={size}
        style={{ width: size, height: size }}
        className={`rounded-full object-cover ${ringCls} ${className}`}
        data-testid={`avatar-${(name || "unknown").toLowerCase().replace(/\s+/g, "-")}`}
      />
    );
  }

  const fontSize = Math.round(size * 0.4);
  return (
    <span
      role="img"
      aria-label={name || ""}
      title={name || ""}
      style={{
        width: size,
        height: size,
        backgroundColor: bg,
        color: fg,
        fontSize,
      }}
      className={`inline-flex items-center justify-center rounded-full font-medium tracking-tight select-none ${ringCls} ${className}`}
      data-testid={`avatar-fallback-${(name || "unknown").toLowerCase().replace(/\s+/g, "-")}`}
    >
      {initials}
    </span>
  );
}

function initialsFrom(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function normalizeSrc(picture) {
  if (!picture) return null;
  // Discard the old pravatar placeholders — they're stock faces and read as fake.
  if (picture.includes("pravatar.cc")) return null;
  return picture;
}

// People are content, not boutique status indicators. Keep their fallbacks
// neutral so a page of teammates does not inherit a whole-market color cast.
const TINT = {
  PLT: "#e8edf6", FLL: "#e8edf6", WYN: "#e8edf6", PNC: "#e8edf6",
  RNO: "#e8edf6", CC: "#e8edf6", Rainbow: "#e8edf6",
};

// Shared slate foreground preserves contrast without suggesting a state.
const FG = {
  PLT: "#475569", FLL: "#475569", WYN: "#475569", PNC: "#475569",
  RNO: "#475569", CC: "#475569", Rainbow: "#475569",
};
